import { functions, firebaseInitialized } from '../firebase';
import { httpsCallable } from 'firebase/functions';

/**
 * Subscribe an email to the newsletter using Firebase Cloud Function
 * @param {string} email - Email address to subscribe
 * @param {string} source - Source of the subscription (e.g., 'home', 'landing_page')
 * @param {string} consentVersion - Version of consent text (e.g., 'v1.0')
 * @param {string} recaptchaToken - reCAPTCHA verification token
 * @returns {Promise<{success: boolean, message: string}>}
 */
export async function subscribeToNewsletter(email, source = 'home', consentVersion = 'v1.0', recaptchaToken = null) {
  if (!firebaseInitialized || !functions) {
    return { success: false, message: 'Firebase is not initialized' };
  }

  if (!email || !email.includes('@')) {
    return { success: false, message: 'Invalid email address' };
  }

  try {
    const subscribeNewsletter = httpsCallable(functions, 'subscribeNewsletter');
    const result = await subscribeNewsletter({
      email: email,
      source: source,
      consentVersion: consentVersion,
      recaptchaToken: recaptchaToken
    });

    const data = result.data;
    return {
      success: data.success || false,
      message: data.message || 'Subscription completed'
    };
  } catch (error) {
    console.error('Error subscribing to newsletter:', error);
    
    // Handle Firebase Functions errors
    if (error.code === 'functions/invalid-argument') {
      return { success: false, message: error.message || 'Invalid email address' };
    }
    
    return { success: false, message: 'Failed to subscribe. Please try again later.' };
  }
}

/**
 * Unsubscribe an email from the newsletter using Firebase Cloud Function
 * @param {string} email - Email address to unsubscribe
 * @returns {Promise<{success: boolean, message: string}>}
 */
export async function unsubscribeFromNewsletter(email) {
  if (!firebaseInitialized || !functions) {
    return { success: false, message: 'Firebase is not initialized' };
  }

  if (!email || !email.includes('@')) {
    return { success: false, message: 'Invalid email address' };
  }

  try {
    const unsubscribeNewsletter = httpsCallable(functions, 'unsubscribeNewsletter');
    const result = await unsubscribeNewsletter({
      email: email
    });

    const data = result.data;
    return {
      success: data.success || false,
      message: data.message || 'Unsubscription completed'
    };
  } catch (error) {
    console.error('Error unsubscribing from newsletter:', error);
    
    // Handle Firebase Functions errors
    if (error.code === 'functions/invalid-argument') {
      return { success: false, message: error.message || 'Invalid email address' };
    }
    
    if (error.code === 'functions/not-found') {
      return { success: false, message: error.message || 'Email not found in our mailing list' };
    }
    
    return { success: false, message: 'Failed to unsubscribe. Please try again later.' };
  }
}

/**
 * Check if an email is subscribed using Firebase Cloud Function
 * @param {string} email - Email address to check
 * @returns {Promise<{subscribed: boolean, exists: boolean}>}
 */
export async function checkSubscriptionStatus(email) {
  if (!firebaseInitialized || !functions) {
    console.error('Firebase not initialized for checkSubscriptionStatus');
    return { subscribed: false, exists: false };
  }

  if (!email || !email.includes('@')) {
    console.error('Invalid email format:', email);
    return { subscribed: false, exists: false };
  }

  try {
    console.log('Calling checkNewsletterStatus function for email:', email);
    const checkNewsletterStatus = httpsCallable(functions, 'checkNewsletterStatus');
    const result = await checkNewsletterStatus({
      email: email
    });

    console.log('checkNewsletterStatus result:', result);
    const data = result.data;
    console.log('checkNewsletterStatus data:', data);
    
    const status = {
      subscribed: data.subscribed || false,
      exists: data.exists || false
    };
    
    console.log('Returning subscription status:', status);
    return status;
  } catch (error) {
    console.error('Error checking subscription status:', error);
    console.error('Error details:', {
      code: error.code,
      message: error.message,
      details: error.details,
      stack: error.stack
    });
    
    // Handle Firebase Functions errors
    if (error.code === 'functions/invalid-argument') {
      console.error('Invalid argument error:', error.message);
      return { subscribed: false, exists: false };
    }
    
    if (error.code === 'functions/not-found') {
      console.error('Function not found - may not be deployed');
      return { subscribed: false, exists: false };
    }
    
    return { subscribed: false, exists: false };
  }
}
